import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Timer, LogOut, Target, CheckCircle, X, WifiOff, UserX } from 'lucide-react';
import { SESSION_STATUS, PARTICIPANT_STATUS } from '../constants';
import { findParticipant as findAnyParticipant, findActiveParticipant, countActiveParticipants } from '../utils/participantUtils';
import { toast } from './notifications';
import { apiService, updateSessionGoal } from '../services/api';
import SessionFeedback from './SessionFeedback';
import { useNavigate } from 'react-router-dom';
import SessionCompletedAnimation from './session/SessionCompletedAnimation';
import { ButtonLoader } from './ui/LoadingSpinner';
import WaitingSessionStatus from './WaitingSessionStatus';
import Button from './ui/Button';
import Modal from './ui/Modal.jsx';

import SessionHeader from './session/SessionHeader';
import SessionInfoCards from './session/SessionInfoCards';
import ShareSessionCard from './session/ShareSessionCard.jsx';
import ValidationSection from './session/ValidationSection';
import ParticipantsList from './session/ParticipantsList';
import { scrollWindowToTop } from '../utils/scrollPosition';

const SessionStatus = ({ session, onEndSession, currentUser, onClose, onFeedbackStateChange, onRefresh, onJoinSuccess }) => {
  const navigate = useNavigate();
  // Robuster ID-Vergleich: String-Konvertierung fuer sicheren Vergleich
  const currentId = currentUser?.id ? String(currentUser.id) : null;
  // Aktive Teilnahme (schliesst LEFT/KICKED/REJECTED aus) - fuer Join-/Validierungs-Logik.
  const findParticipant = (participants) => findActiveParticipant(participants, currentId);
  const isCurrentUser = (id) => currentId && String(id) === currentId;

  // Basisüberprüfungen gleich am Anfang durchführen
  const initialParticipants = session?.participants || [];
  const initialUserParticipant = findParticipant(initialParticipants);
  const initialIsParticipant = Boolean(initialUserParticipant);

  // Zustände definieren
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [validationCode, setValidationCode] = useState('');
  const [validationError, setValidationError] = useState('');
  const [focusStartTime, setFocusStartTime] = useState(Date.now());
  const [totalFocusTime, setTotalFocusTime] = useState(0);
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [lastSyncTime, setLastSyncTime] = useState(Date.now());
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // Beenden und Abbrechen liefen bis August 2026 ueber window.confirm - obwohl fuer
  // "Verlassen" direkt daneben schon Modal.Confirm stand. Das native Fenster laesst sich
  // nicht gestalten, ignoriert Dark-Mode und sieht in der nativen App wie ein
  // Systemfehler aus.
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // ✅ State für interaktive Lernziele
  const [sessionGoals, setSessionGoals] = useState(session?.goals || []);
  const [goalsUpdating, setGoalsUpdating] = useState({});

  // ✅ isHost früh berechnen (wird für handleSessionEnd benötigt)
  const isHost = isCurrentUser(session?.hostId);

  // Synchronisiere Goals wenn Session sich ändert
  useEffect(() => {
    if (session?.goals) {
      setSessionGoals(session.goals);
    }
  }, [session?.goals]);

  // ✅ Funktion zum Abhaken eines Lernziels
  const handleToggleGoal = async (goalId, currentCompleted) => {
    const newCompleted = !currentCompleted;

    // Optimistic UI Update
    setSessionGoals(prev => prev.map(goal =>
      goal.id === goalId ? { ...goal, completed: newCompleted } : goal
    ));
    setGoalsUpdating(prev => ({ ...prev, [goalId]: true }));

    try {
      await updateSessionGoal(session.id, goalId, { completed: newCompleted });
    } catch (error) {
      // Rollback bei Fehler
      setSessionGoals(prev => prev.map(goal =>
        goal.id === goalId ? { ...goal, completed: currentCompleted } : goal
      ));
      console.error('Fehler beim Aktualisieren des Lernziels:', error);
      toast.error('Lernziel konnte nicht aktualisiert werden');
    } finally {
      setGoalsUpdating(prev => ({ ...prev, [goalId]: false }));
    }
  };

  // Verbesserte Fokuszeit-Synchronisation
  const syncFocusTime = useCallback(() => {
    // Überprüfen, ob die Session existiert und aktiv ist
    if (!session) {
      console.warn('Keine Session vorhanden für Fokus-Synchronisation');
      return;
    }

    if (session.status !== SESSION_STATUS.ACTIVE) {
      return;
    }

    // Hier direkt auf die Session-Teilnehmer zugreifen, um initialIsParticipant zu umgehen
    const participants = session.participants || [];
    const userParticipant = findParticipant(participants);
    const isParticipantActive = Boolean(userParticipant);

    if (!isParticipantActive) {
      console.warn('Konto ist nicht aktiv in dieser Session');
      return;
    }

    const now = Date.now();
    const timeSinceLastSync = now - lastSyncTime;

    if (isWindowFocused && timeSinceLastSync > 0) {
      setTotalFocusTime(prev => prev + timeSinceLastSync);
    }

    setLastSyncTime(now);
  }, [session, currentUser, isWindowFocused, lastSyncTime]);

  // Automatische Synchronisation alle 30 Sekunden
  useEffect(() => {
    const syncInterval = setInterval(syncFocusTime, 30000);
    return () => clearInterval(syncInterval);
  }, [syncFocusTime]);

  // Verbesserte Fenster-Fokus-Erkennung
  useEffect(() => {
    // Nur für aktive Sessions Fokus-Tracking aktivieren
    if (!session || session.status !== SESSION_STATUS.ACTIVE) {
      return;
    }

    const handleVisibilityChange = () => {
      const now = Date.now();

      if (document.hidden) {
        setIsWindowFocused(false);
        syncFocusTime();
      } else {
        setIsWindowFocused(true);
        setFocusStartTime(now);
      }
    };

    const handleBlur = () => {
      setIsWindowFocused(false);
      syncFocusTime();
    };

    const handleFocus = () => {
      setIsWindowFocused(true);
      setFocusStartTime(Date.now());
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    // Set initial timer state
    if (session?.status === SESSION_STATUS.ACTIVE) {
      setIsTimerActive(true);
      setFocusStartTime(Date.now());
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [session, syncFocusTime]);

  // Online/Offline-Status fuer sichtbares Verbindungs-Banner (Schul-WLAN bricht oft ab)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Timer-Logik
  // FOCUS-MODUS: Falls startTime nicht gesetzt ist, nutze aktuelle Zeit als Start
  useEffect(() => {
    let timer;
    if (isTimerActive) {
      const startTime = session?.startTime
        ? new Date(session.startTime).getTime()
        : Date.now(); // Fallback für Focus-Modus wenn startTime fehlt

      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [isTimerActive, session]);

  // Verbesserte Session-Aktivitätserkennung
  // ✅ FIXED (1. Nov 2025): Timer startet IMMER bei ACTIVE Status
  // Vorher: hasValidated Check blockierte Timer-Start bei Focus-Sessions ohne Validierung
  useEffect(() => {
    if (session?.status === SESSION_STATUS.ACTIVE) {
      setIsTimerActive(true);
      const now = Date.now();
      setFocusStartTime(now);
      setLastSyncTime(now);
    }
  }, [session]);

  // Feedback Modal State an Parent Component propagieren
  useEffect(() => {
    if (onFeedbackStateChange) {
      onFeedbackStateChange(showFeedback);
    }
  }, [showFeedback, onFeedbackStateChange]);

  // ✅ Auto-Show Feedback für COMPLETED Sessions ohne User-Feedback
  // CRITICAL FIX: Zeige Feedback auch für Teilnehmer wenn Host die Session beendet hat!
  //
  // ⚠️ WICHTIG: Nur triggern wenn die Session GERADE ERST beendet wurde
  // (<15 min her). Sonst öffnet ein Klick auf eine alte SESSION_COMPLETED-
  // Benachrichtigung erneut den Feedback-Flow — das wollen wir nicht.
  useEffect(() => {
    if (session?.status === SESSION_STATUS.COMPLETED) {
      // User hat bereits Feedback gegeben → Zeige Completion-Message und schließe
      if (session.currentUserHasFeedback === true) {
        // Kein Toast: Die Ansicht schliesst gleich, die Session steht im Logbuch.
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
        return;
      }

      // Frische-Check: Nur auto-triggern wenn Session eben erst beendet wurde
      const FRESH_WINDOW_MS = 15 * 60 * 1000;
      const endTs = session.endTime ? new Date(session.endTime).getTime() : null;
      const isFresh = endTs == null || (Date.now() - endTs) < FRESH_WINDOW_MS;
      if (!isFresh) {
        return;
      }

      // User hat noch KEIN Feedback gegeben → Zeige Feedback-Formular
      // CRITICAL FIX: Verwende !session.currentUserHasFeedback statt === false
      // Das funktioniert auch wenn das Feld undefined ist
      if (!session.currentUserHasFeedback && !showFeedback && !showCompletionAnimation) {

        // Toast für Teilnehmer anzeigen
        const isHost = isCurrentUser(session.hostId);
        if (!isHost) {
          toast.info('Der Host hat die Session beendet. Wie lief es für dich?', {
            autoClose: 5000
          });
        }

        // Zeige erst Animation, dann Feedback
        if (session.startTime) {
          const endPoint = session.endTime ? new Date(session.endTime).getTime() : Date.now();
          const sessionDuration = endPoint - new Date(session.startTime).getTime();

          // ✅ FIX: Echte Werte aus DB-Participant-Daten nutzen statt Hardcoded-Defaults
          // Ungefiltert: nach Sessionende ist der eigene Record bewusst LEFT
          const myParticipant = findAnyParticipant(session.participants || [], currentId);
          // focusTime steht in der DB in SEKUNDEN (Export: focusTimeSeconds). Bis Okt 2026
          // stand hier "* 60000" (Minuten) - fiel nicht auf, weil focusTime bei
          // Fokus-Sessions nie gespeichert wurde und immer 0 war.
          const dbFocusTime = myParticipant?.focusTime ? myParticipant.focusTime * 1000 : null;
          const dbFocusStats = myParticipant?.focusStats;
          const realFocusRatio = dbFocusStats?.focusRatio
            || (dbFocusTime && sessionDuration > 0 ? Math.min(dbFocusTime / sessionDuration, 1) : null);

          // Der frueher hier berechnete bonusMultiplier ist entfallen: Fokus wirkt
          // sich nicht mehr auf die Aura aus. focusRatio bleibt fuer die reine
          // Fokuszeit-Anzeige in der Abschluss-Animation. Ohne Messung bleibt sie
          // leer: Der fruehere Ersatzwert 0.85 zeigte "Fokus 85 %" ohne Grundlage und
          // ging mit dem Logbuch an den Server (backend/src/utils/focusCapture.js
          // nimmt ihn deshalb nicht als Messung).
          setSessionStats({
            totalFocusTime: dbFocusTime,
            focusRatio: realFocusRatio,
            dbAuraEarned: myParticipant?.auraEarned // Echten DB-Wert durchreichen
          });

          setShowCompletionAnimation(true);
          // 🔝 Scroll nach oben für bessere UX (besonders auf Mobile)
          scrollWindowToTop('smooth');
        } else {
          // Keine StartTime vorhanden - direkt zum Feedback
          setShowFeedback(true);
          // 🔝 Scroll nach oben für bessere UX (besonders auf Mobile)
          scrollWindowToTop('smooth');
        }
      }
    }
  }, [session, showFeedback, showCompletionAnimation, onClose, currentUser]);

  // Session Start Handler für Host
  const handleSessionStart = async (sessionId) => {
    try {
      const response = await apiService.startSession(sessionId);

      if (response.success) {
        // Sofort neu laden statt bis zu 15s auf den Poll zu warten
        if (typeof onRefresh === 'function') onRefresh();
      } else {
        toast.error('Session konnte nicht gestartet werden');
      }
    } catch (error) {
      console.error('Fehler beim Starten der Session:', error);
      // Die Meldung des Servers zeigen (z. B. 409 "Du bist bereits in der aktiven
      // Session …"), sonst blieb unklar, warum der Start nicht ging.
      toast.error(error.response?.data?.error || 'Fehler beim Starten der Session');
    }
  };

  // Der eigentliche Abschluss - aus handleSessionEnd herausgezogen, weil ihn jetzt
  // zwei Wege ausloesen: der Teilnehmer direkt, der Host nach der Rueckfrage im Modal.
  const finishSession = () => {
    // Fokuszeit synchronisieren, bevor die Session beendet wird
    syncFocusTime();

    const finalFocusDuration = isWindowFocused
      ? (Date.now() - focusStartTime) + totalFocusTime
      : totalFocusTime;

    // Die Fokus-Quote wird nur noch fuer echte Fokus-Sessions (Solo, isFocusMode)
    // berechnet. Sie setzt die Vordergrundzeit des Tabs ins Verhaeltnis zur
    // Session-Dauer - bei einer Lern-Session zu zweit misst das, wie lange jemand
    // NICHT mit dem Lernpartner geredet hat. Der Wert landete trotzdem im
    // Abschluss-Screen ("Fokus 3%"), weil beim Entfernen des Gruppen-Fokus im Juli 2026
    // nur der Tracker abgeschaltet wurde, nicht diese Rechnung.
    const sessionDuration = Date.now() - new Date(session.startTime).getTime();
    const focusRatio = session.isFocusMode && sessionDuration > 0
      ? finalFocusDuration / sessionDuration
      : null;

    // Aura gibt es erst ab 2 validierten Teilnehmenden (Solo-Exploit-Schutz im
    // Backend). Ohne diese Pruefung zeigte die Animation eine geschaetzte
    // Aura-Zahl an, und die Benachrichtigung danach meldete 0 - der Host sah
    // beim Alleine-Beenden erst eine Belohnung und dann deren Gegenteil.
    const validatedCount = (session.participants || [])
      .filter(p => String(p.status || '').toUpperCase() === 'VALIDATED').length;

    setSessionStats({
      totalFocusTime: finalFocusDuration,
      focusRatio,
      // Fuer den Rueckfall-Grund in der Abschluss-Anzeige (utils/auraReason.js)
      validatedCount,
      ...(validatedCount < 2 ? { dbAuraEarned: 0 } : {}),
    });

    setShowCompletionAnimation(true);
  };

  // ✅ FIX: handleSessionEnd VOR dem frühen Return definieren (TDZ-Problem behoben)
  const handleSessionEnd = async (type) => {
    try {
      // Sicherstellen, dass die Session existiert
      if (!session) {
        console.error('Keine Session zum Beenden vorhanden');
        return;
      }

      if (type === 'complete') {
        // Klare Rueckfrage: Beenden ist endgueltig und verteilt Aura.
        if (isHost) {
          setShowCompleteConfirm(true);
          return;
        }
        finishSession();
        return;
      }

      if (type === 'confirmComplete') {
        finishSession();
        return;
      }

      if (type === 'leave') {
        setShowLeaveConfirm(true);
        return;
      }

      if (type === 'confirmLeave') {
        // ✅ FIX: Verwende den korrekten 'confirmLeave' Action statt 'update'
        // SessionDetailPage wird dann apiService.leaveSession() aufrufen
        await onEndSession('confirmLeave');
        onClose();
        return;
      }

      if (type === 'cancel' && isHost) {
        setShowCancelConfirm(true);
        return;
      }

      if (type === 'confirmCancel') {
        await onEndSession('cancel');
        onClose();
        return;
      }

      // Für alle anderen Aktionen die Session beenden
      if (onEndSession) {
        await onEndSession(type);
      } else {
        console.warn('onEndSession-Funktion nicht verfügbar');
      }
    } catch (error) {
      console.error('Fehler beim Beenden der Session:', error);
      toast.error('Die Session konnte nicht beendet werden. Bitte versuche es erneut.');
    }
  };

  // Entfernte Teilnehmer bekommen eine eigene Erklaerseite - vor allen anderen
  // Zweigen, damit sie weder Warteraum noch Code-Eingabe noch einen Verlassen-Knopf
  // sehen. Vorher zaehlte KICKED als "nicht dabei", wodurch ihnen ein scharfer
  // Beitreten-Knopf angeboten wurde, der serverseitig garantiert scheiterte.
  // `viewerStatus` liefert GET /sessions/:id (normalisiert, null = nie dabei).
  const viewerStatus = session?.viewerStatus
    ? String(session.viewerStatus).toUpperCase()
    : null;
  const wasRemoved = viewerStatus === PARTICIPANT_STATUS.KICKED
    || viewerStatus === PARTICIPANT_STATUS.REJECTED;

  if (session && !isHost && wasRemoved) {
    const hostName = (session.participants || []).find(p => p.isHost)?.name
      || session.tenant_users?.tenant_profiles?.displayName
      || 'Der Host';
    const removedByKick = viewerStatus === PARTICIPANT_STATUS.KICKED;

    return (
      <div className="space-y-4">
        <div className="bg-surface border border-border p-6 rounded-xl text-center space-y-3">
          <div className="w-12 h-12 bg-warning-soft rounded-full flex items-center justify-center mx-auto">
            <UserX className="h-6 w-6 text-warning" aria-hidden="true" />
          </div>
          <h3 className="font-medium text-foreground">
            {removedByKick
              ? 'Du wurdest aus dieser Session entfernt'
              : 'Deine Teilnahme wurde abgelehnt'}
          </h3>
          <p className="text-sm text-muted-foreground">
            Nur {hostName} kann dich wieder zulassen. {hostName} wurde darüber
            informiert, dass du zurück möchtest.
          </p>
          <Button variant="secondary" size="lg" onClick={() => navigate('/sessions')} className="w-full">
            Zurück zur Übersicht
          </Button>
        </div>
      </div>
    );
  }

  // Im Warteraum heissen die Knoepfe "absagen" (die Session laeuft ja noch nicht),
  // in der laufenden Session "abbrechen"/"verlassen". Die Dialoge muessen dieselbe
  // Sprache sprechen wie der Knopf, der sie geoeffnet hat.
  const isPlannedSession = session?.status === SESSION_STATUS.WAITING
    || session?.status === SESSION_STATUS.PLANNED;

  // Verlassen- und Abbrechen-Dialog werden in BEIDEN Zweigen gebraucht.
  // Sie standen bis September 2026 nur im Haupt-Return - der Warteraum verlaesst
  // die Komponente aber vorher mit einem eigenen return. Ergebnis: "Teilnahme
  // absagen" und "Session absagen" setzten dort zwar den State, das Modal wurde
  // aber nie gerendert. Beide Knoepfe taten schlicht nichts.
  const confirmDialogs = (
    <>
      {/* Abbrechen — ohne Aura, ohne Logbuch-Eintrag.
          Beide Knoepfe hiessen hier bis September 2026 "Abbrechen": Modal.Confirm
          beschriftet seinen Schliessen-Knopf standardmaessig so, und confirmLabel
          stand ebenfalls auf "Abbrechen". Im Dialog standen damit zwei gleich
          benannte Knoepfe nebeneinander - der linke schloss nur das Fenster. Wer
          ihn drueckte (und das ist der naheliegende), sah: nichts passiert.
          Deshalb sagen beide Beschriftungen jetzt, was sie tun. */}
      <Modal.Confirm
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          handleSessionEnd('confirmCancel');
        }}
        title={isPlannedSession ? 'Session absagen?' : 'Session abbrechen?'}
        confirmLabel={isPlannedSession ? 'Ja, Session absagen' : 'Ja, Session abbrechen'}
        cancelLabel="Nein, zurück"
        variant="danger"
      >
        <p className="text-muted-foreground">
          Es gibt keine Aura und keinen Logbuch-Eintrag. Alle Teilnehmenden werden benachrichtigt.
        </p>
      </Modal.Confirm>

      {/* Verlassen — auf ui/Modal.Confirm (Focus-Trap + Escape gratis) */}
      <Modal.Confirm
        open={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => {
          setShowLeaveConfirm(false);
          handleSessionEnd('confirmLeave');
        }}
        title={isPlannedSession ? 'Teilnahme absagen?' : 'Session verlassen?'}
        confirmLabel={isPlannedSession ? 'Ja, Teilnahme absagen' : 'Ja, verlassen'}
        cancelLabel="Nein, zurück"
        variant="danger"
      >
        <p className="text-muted-foreground">
          {isPlannedSession
            ? 'Dein reservierter Platz wird wieder frei. Du kannst später erneut beitreten, solange noch Plätze frei sind.'
            : 'Möchtest du die Session wirklich verlassen? Du kannst später wieder beitreten, solange die Session noch aktiv ist.'}
        </p>
      </Modal.Confirm>
    </>
  );

  // Zeige Warteraum für geplante/wartende Sessions
  if (session?.status === SESSION_STATUS.WAITING || session?.status === SESSION_STATUS.PLANNED) {
    return (
      <>
      <WaitingSessionStatus
        session={session}
        isHost={isHost}
        currentUserId={currentUser?.id}
        onStart={handleSessionStart}
        onRefresh={onRefresh}
        onValidate={async (code) => {
          try {
            const response = await apiService.verifySessionCode(code);

            if (response.success) {
              // Erfolgs-Overlay (grüner Haken + hüpfender Otter) statt nur Toast.
              // Fallback auf Toast, falls kein Overlay-Handler übergeben wurde.
              if (typeof onJoinSuccess === 'function') {
                onJoinSuccess();
              } else {
                toast.success('Code bestätigt. Du bist dabei.');
                if (typeof onRefresh === 'function') onRefresh();
              }
            } else {
              toast.error(response.error || 'Ungültiger Code');
              throw new Error(response.error || 'Ungültiger Code');
            }
          } catch (error) {
            console.error('Fehler bei Code-Validierung:', error);
            if (!error.message?.includes('Ungültiger Code')) {
              toast.error(error.response?.data?.error || 'Ungültiger oder abgelaufener Code');
            }
            throw error;
          }
        }}
        onCancel={() => handleSessionEnd(isHost ? 'cancel' : 'leave')}
      />
      {confirmDialogs}
      </>
    );
  }

  // Stellen Sie sicher, dass session existiert
  if (!session) {
    console.error("Session ist undefined");
    return <div className="p-4">Fehler beim Laden der Session</div>;
  }

  // Aktualisierte Variablen verwenden für den Rest der Komponente
  const participants = session.participants || [];
  // isHost ist bereits früher definiert (Zeile 46)
  const userParticipant = findParticipant(participants);
  const isParticipant = Boolean(userParticipant);

  // Validierungslogik für Gruppensessions
  const hasValidParticipants = participants.some(p => p.status === PARTICIPANT_STATUS.VALIDATED);
  const canSeeCode = isHost || userParticipant?.status === PARTICIPANT_STATUS.VALIDATED;

  // Wer aktiv in der Session steht und noch nicht validiert ist, braucht die Code-Eingabe -
  // egal mit welchem Status (PENDING beim Selbst-Beitritt, RESERVED bei einer Einladung
  // durch den Host, JOINED aus Altdaten). Vorher wurde hier nur PENDING abgefragt; ein
  // eingeladener Lernpartner fiel dadurch durch alle Zweige und landete in der Sackgasse
  // unten ("Du bist nicht (mehr) Teilnehmer" + "Session ist voll" - obwohl der zweite
  // belegte Platz sein eigener war).
  const needsValidation = isParticipant && !canSeeCode;
  const canJoinSession = !isParticipant && countActiveParticipants(participants) < session.maxParticipants;

  // Mindestdauer-Prüfung: 10 Minuten (600000 ms)
  const MIN_SESSION_DURATION_MS = 10 * 60 * 1000;
  const sessionDuration = session.startTime
    ? Date.now() - new Date(session.startTime).getTime()
    : 0;
  const canEndSession = sessionDuration >= MIN_SESSION_DURATION_MS;

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Holt den reinen Zahlencode aus dem, was im Eingabefeld landet. Ein gescannter QR-Code
  // kommt je nach Quelle als URL oder als altes JSON-Format herein.
  const extractSessionCode = (code) => {
    const raw = (code || '').trim();

    // QR-Code im URL-Format: eachotter://session/SESSION_ID/CODE
    if (raw.startsWith('eachotter://')) {
      const parts = raw.split('/');
      return parts[parts.length - 1];
    }

    // Altes JSON-Format: {"code":"123456"}
    if (raw.startsWith('{')) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.code) return String(parsed.code);
      } catch {
        // bleibt wie es ist
      }
    }

    return raw;
  };

  // Einziger Weg fuer die Code-Eingabe - fuer Beitritt UND Validierung.
  // WICHTIG: verifySessionCode statt validateParticipant. POST /sessions/verify-code stuft
  // RESERVED | PENDING | LEFT zu VALIDATED hoch und deckt zusaetzlich den Neu-Beitritt ab;
  // die alte Route /sessions/:id/validate lehnte eingeladene (RESERVED) Partner ab.
  const handleJoinWithCode = async (code) => {
    setValidationError('');
    const cleanCode = extractSessionCode(code);

    try {
      const response = await apiService.verifySessionCode(cleanCode);

      if (response.success) {
        if (typeof onJoinSuccess === 'function') {
          onJoinSuccess();
        } else {
          toast.success('Code bestätigt. Du bist dabei.');
          if (typeof onRefresh === 'function') onRefresh();
        }
      } else {
        setValidationError(response.error || 'Ungültiger Code');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || 'Ungültiger oder abgelaufener Code';
      setValidationError(errorMessage);
      console.error('Fehler beim Session-Beitritt mit Code:', error);
    }
  };

  // Handler: Teilnehmer aus der Session entfernen (Host only)
  const handleKickParticipant = async (userId) => {
    try {
      await apiService.removeParticipant(session.id, userId);
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Fehler beim Entfernen des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Entfernen der Person');
    }
  };

  // Handler: Teilnehmer manuell als anwesend markieren (Host only, z.B. Schueler ohne Geraet)
  const handleMarkPresent = async (userId) => {
    try {
      await apiService.markParticipantPresent(session.id, userId);
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Fehler beim Markieren des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Markieren als anwesend');
    }
  };

  // Handler: entfernten Teilnehmer wieder zulassen (Host only).
  // Gab es bisher nur im Warteraum - in der laufenden Session war der Weg zurueck
  // nicht erreichbar, obwohl der Endpunkt existiert.
  const handleReadmitParticipant = async (userId) => {
    try {
      await apiService.readmitParticipant(session.id, userId);
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Fehler beim Wieder-Zulassen des Teilnehmers:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Wieder-Zulassen');
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    const enhancedFeedback = {
      ...feedbackData,
      focusStats: sessionStats  // Verwende die bereits berechneten Statistiken
    };

    // ✅ FIX: Unterscheide zwischen Host und Teilnehmer
    const isHost = isCurrentUser(session.hostId);

    if (isHost) {
      // Host beendet die Session MIT Feedback
      await onEndSession('complete', enhancedFeedback);
    } else {
      // Teilnehmer sendet nur Feedback (Session bleibt für andere aktiv)
      await onEndSession('submitFeedback', enhancedFeedback);
    }

    onClose();
  };

  // Funktion zum Übergang von der Animation zum Feedback
  const handleAnimationComplete = () => {
    setShowCompletionAnimation(false);
    setShowFeedback(true);
  };

  if (showCompletionAnimation && sessionStats) {
    const endPoint = session.endTime ? new Date(session.endTime).getTime() : Date.now();
    const durationMs = endPoint - new Date(session.startTime).getTime();

    // Aura-Zahl nur, wenn sie feststeht: aus der DB (nach Auto-Complete bzw. wenn
    // der Host beendet hat) oder 0 bei zu wenigen Validierten. Beim manuellen
    // Beenden laeuft der Server-Abschluss erst mit dem Feedback; bis Okt 2026 stand
    // hier eine lokale Schaetzung (Minuten x 1,2), die Mindestdauer, Tageslimit und
    // Deckel nicht kannte ("+6 Aura" fuer 5 Minuten, danach 0). Jetzt: null =
    // noch offen, die Anzeige sagt das so.
    const auraToShow = sessionStats.dbAuraEarned != null ? sessionStats.dbAuraEarned : null;

    return (
      <SessionCompletedAnimation
        session={{
          ...session,
          duration: durationMs,
          auraEarned: auraToShow,
          // Grund fuer 0 Aura vom Server, falls die Session ihn mitbringt;
          // sonst leitet die Anzeige ab, was sicher ist (validatedCount, Dauer)
          auraReason: session.auraReason,
          validatedCount: sessionStats.validatedCount,
          focusStats: sessionStats
        }}
        onContinue={handleAnimationComplete}
      />
    );
  }

  if (showFeedback) {
    return (
      <SessionFeedback
        session={session}
        onSubmit={handleFeedbackSubmit}
      // ❌ ENTFERNT: onCancel - Modal kann nicht mehr weggeklickt werden!
      // User MUSS Feedback abschicken um Session zu beenden
      />
    );
  }

  return (
    // Kein eigener Hintergrund und keine eigene Breite mehr: Den Rahmen setzt
    // die Seite (PageShell -> max-w-3xl, px-4, pt-6). Die fruehere Kombination
    // aus `bg-surface-raised` plus zweitem `max-w-2xl mx-auto px-4 py-4` legte
    // INNERHALB dieses Rahmens noch einen zweiten an - sichtbar als abgesetzter
    // Kasten mit doppeltem Innenabstand, der an keiner anderen Stelle der App
    // vorkommt.
    <div className="space-y-4 sm:space-y-6">
      <SessionHeader hasValidParticipants={hasValidParticipants} />
        {!isOnline && (
          <div className="flex items-center justify-center gap-2 bg-warning-soft text-warning border border-warning/30 p-4 rounded-xl text-center font-medium">
            <WifiOff className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Keine Internetverbindung — deine Anwesenheit wird gespeichert, sobald du wieder online bist.
          </div>
        )}

        {/* Laufende offene Session mit freiem Platz: Host kann den Link teilen (Nov 2026) */}
        {isHost && <ShareSessionCard session={session} currentUserId={currentUser?.id} />}

        {/* Zeige Session Info wenn User berechtigt ist */}
        {canSeeCode ? (
          <SessionInfoCards
            session={session}
            showQR={showQR}
            setShowQR={setShowQR}
            isTimerActive={isTimerActive}
            elapsedTime={elapsedTime}
            formatTime={formatTime}
            isHost={isHost}
          />
        ) : (needsValidation || canJoinSession) ? (
          // Beide Faelle bekommen dieselbe Code-Eingabe:
          //  - needsValidation: schon eingetragen, aber noch nicht validiert
          //    (PENDING beim Selbst-Beitritt, RESERVED bei Einladung durch den Host)
          //  - canJoinSession:  noch gar nicht dabei (inkl. LEFT nach Verlassen)
          // Bewusst immer ueber handleJoinWithCode -> POST /sessions/verify-code: Diese
          // Route stuft RESERVED | PENDING | LEFT zu VALIDATED hoch. Die frueher hier
          // genutzte Route POST /sessions/:id/validate lehnte RESERVED dagegen ab, sodass
          // eingeladene Partner selbst mit sichtbarem Eingabefeld nicht hineingekommen waeren.
          <ValidationSection
            validationCode={validationCode}
            setValidationCode={setValidationCode}
            handleValidateCode={handleJoinWithCode}
            onOpenScanner={() => navigate('/scan-qr')}
            validationError={validationError}
            session={session}
          />
        ) : (
          <div className="bg-surface border border-border p-6 rounded-xl text-center space-y-2">
            <div className="w-12 h-12 bg-warning-soft rounded-full flex items-center justify-center mx-auto">
              <Timer className="h-6 w-6 text-warning" />
            </div>
            <h3 className="font-medium text-foreground">Du gehörst nicht (mehr) zu dieser Session</h3>
            <p className="text-sm text-muted-foreground">
              {countActiveParticipants(participants) >= session.maxParticipants
                ? 'Die Session ist voll – ein Beitritt ist gerade nicht möglich.'
                : 'Ein Beitritt ist gerade nicht möglich.'}
            </p>
          </div>
        )}

        {/* ✅ Interaktive Lernziele - nur für Host oder validierte Teilnehmer */}
        {sessionGoals && sessionGoals.length > 0 && (isHost || userParticipant?.status === PARTICIPANT_STATUS.VALIDATED) && (
          <div className="bg-surface border border-primary/30 rounded-xl p-4 space-y-3 shadow-token-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-soft rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Lernziele für diese Session</h3>
                  <p className="text-xs text-muted-foreground">
                    {sessionGoals.filter(g => g.completed).length} von {sessionGoals.length} erreicht
                  </p>
                </div>
              </div>
              {/* Fortschrittsanzeige */}
              <div className="flex items-center gap-1" aria-hidden="true">
                {sessionGoals.map((goal, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${goal.completed ? 'bg-success' : 'bg-muted-foreground/30'
                      }`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {sessionGoals.map((goal, index) => (
                <button
                  key={goal.id || index}
                  onClick={() => goal.id && handleToggleGoal(goal.id, goal.completed)}
                  disabled={!goal.id || goalsUpdating[goal.id]}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${goal.completed
                    ? 'bg-success-soft border border-success/30'
                    : 'bg-surface-raised border border-border hover:bg-primary-soft hover:border-primary/30'
                    } ${goalsUpdating[goal.id] ? 'opacity-50' : ''}`}
                >
                  {/* Checkbox */}
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${goal.completed
                    ? 'bg-success border-success'
                    : 'border-border hover:border-primary'
                    }`}>
                    {goal.completed && (
                      <CheckCircle className="w-4 h-4 text-success-foreground" />
                    )}
                  </div>

                  {/* Goal Text */}
                  <div className="flex-1 text-left min-w-0">
                    <p className={`text-sm font-medium ${goal.completed ? 'text-success line-through' : 'text-foreground'
                      }`}>
                      {goal.text || goal.title || goal.description}
                    </p>
                    {goal.description && goal.text && (
                      <p className="text-xs text-muted-foreground mt-0.5">{goal.description}</p>
                    )}
                  </div>

                  {/* Loading Indicator */}
                  {goalsUpdating[goal.id] && (
                    <ButtonLoader />
                  )}
                </button>
              ))}
            </div>

            <p className="text-xs text-center text-faint">
              Tippe auf ein Ziel um es abzuhaken
            </p>
          </div>
        )}

        {/* Teilnehmerliste - nur für Host oder validierte Teilnehmer */}
        {(isHost || userParticipant?.status === PARTICIPANT_STATUS.VALIDATED) && (
          <ParticipantsList
            participants={session.participants}
            maxParticipants={session.maxParticipants}
            isHost={isHost}
            currentUserId={currentUser?.id}
            onKickParticipant={isHost ? handleKickParticipant : undefined}
            onMarkPresent={isHost ? handleMarkPresent : undefined}
            onReadmitParticipant={isHost ? handleReadmitParticipant : undefined}
          />
        )}

        <div className="space-y-3">
          {isHost ? (
            <div className="space-y-2">
              {/* Session beenden Button - nur aktiv nach 10 Minuten und mit validierten Teilnehmern */}
              {hasValidParticipants && canEndSession && (
                <Button
                  variant="success"
                  size="lg"
                  onClick={() => handleSessionEnd('complete')}
                  className="w-full"
                >
                  <CheckCircle className="h-5 w-5" />
                  Session beenden (mit Lernlogbuch & Aura)
                </Button>
              )}

              {/* Session abbrechen Button - IMMER verfügbar für Host */}
              <Button variant="secondary" size="lg" onClick={() => handleSessionEnd('cancel')} className="w-full">
                <X className="h-5 w-5" />
                Session abbrechen (ohne Aura)
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                {hasValidParticipants
                  ? (canEndSession
                    ? 'Abbruch ohne Lernlogbuch-Eintrag. Alle Teilnehmenden werden benachrichtigt.'
                    : `Aura erst nach 10 Minuten möglich (${Math.ceil((MIN_SESSION_DURATION_MS - sessionDuration) / 60000)} Min verbleibend). Abbruch ohne Lernlogbuch.`
                  )
                  : 'Die Session wird ohne Aura-Vergabe abgebrochen.'
                }
              </p>
            </div>
          ) : isParticipant ? (
            // An die tatsaechliche Teilnahme gebunden, nicht nur an "kein Host":
            // sonst steht der Verlassen-Knopf auch unter der Karte "Du bist nicht
            // (mehr) Teilnehmer" - und ein Gekickter konnte darueber seinen Status
            // von KICKED auf LEFT umschreiben und wieder hereinkommen.
            <Button variant="secondary" size="lg" onClick={() => handleSessionEnd('leave')} className="w-full">
              <LogOut className="h-5 w-5" />
              Session verlassen
            </Button>
          ) : null}
        </div>

      {/* Beenden — endgueltig, verteilt Aura und legt den Logbuch-Eintrag an */}
      <Modal.Confirm
        open={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={() => {
          setShowCompleteConfirm(false);
          handleSessionEnd('confirmComplete');
        }}
        title="Session für alle beenden?"
        confirmLabel="Beenden"
      >
        <p className="text-muted-foreground">
          Die Aura wird verteilt und der Logbuch-Eintrag angelegt. Das lässt sich nicht rückgängig machen.
        </p>
      </Modal.Confirm>

      {confirmDialogs}
    </div>
  );
};

SessionStatus.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    startTime: PropTypes.string,
    topic: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    participants: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired
    })).isRequired,
    maxParticipants: PropTypes.number
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  onEndSession: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onFeedbackStateChange: PropTypes.func,
  onRefresh: PropTypes.func,
  onJoinSuccess: PropTypes.func,
};

export default SessionStatus;
